import Swal from "sweetalert2";
import api from "../../utils/api";
import i18n from "../../i18n";

export const ARCHIVE_TIME_ZONE = "America/Mexico_City";

export type ArchiveReason = "suspend" | "delete";
export type ArchiveJobReason = ArchiveReason | "transfer";
export type JobStatus = "running" | "done" | "failed" | "deleted";
export type ArchiveSource = "positions" | "events" | "commands" | "geofences";

export interface JobTotals {
  positions: number;
  events: number;
  commands: number;
  geofences: number;
}

export interface DriftDoc {
  day: string | null;
  source: ArchiveSource;
  declared: number;
  stored: number;
  missing: number;
}

export interface ArchiveJob {
  id: string;
  companyId: string;
  companyName: string;
  businessName?: string | null;
  rfc?: string | null;
  email?: string | null;
  reason: ArchiveJobReason;
  sources: string[];
  status: JobStatus;
  deviceCount: number;
  doneCount: number;
  failedCount: number;
  totals: JobTotals;
  createdAt: string;
  exportCsv?: boolean;
  purgeWithoutBackup?: boolean;
  hasDrift?: boolean;
  driftDeviceCount?: number;
  driftMissing?: number;
  duplicatesDropped?: number;
  deferredDocs?: number;
}

export interface DeviceDays {
  positions: string[];
  events: string[];
  commands: string[];
  geofences: string[];
}

export interface DeviceProgress {
  id: string;
  identifier: string;
  status: string;
  error?: string | null;
  days: DeviceDays;
  purgedCounts: Partial<JobTotals>;
  driftDocs?: Record<string, DriftDoc>;
  duplicatesDropped?: number;
  deferredDocs?: number;
}

export interface ArchiveJobDetail extends ArchiveJob {
  devices: DeviceProgress[];
}

export interface ArchiveFileEntry {
  deviceId: string;
  source: ArchiveSource;
  day: string;
  path: string;
  url: string;
}

export interface ArchiveFilesQuery {
  jobId: string;
  deviceId?: string;
  source?: ArchiveSource;
  from?: string;
  to?: string;
}

export async function createArchive(
  companyId: string,
  reason: ArchiveReason,
  sources: ArchiveSource[],
  deviceIds?: string[],
  exportCsv: boolean = true
): Promise<{ jobId: string }> {
  const res = await api.post("/device-archive", {
    companyId,
    reason,
    sources,
    timeZone: ARCHIVE_TIME_ZONE,
    exportCsv,
    ...(deviceIds && deviceIds.length ? { deviceIds } : {}),
  });
  return res.data?.data?.data;
}

const tRespaldos = (key: string) => i18n.t(key, { ns: "respaldos" }) as string;

export function archiveErrorText(err: unknown): string {
  const e = err as {
    response?: {
      data?: { meta?: { message?: string; messageKey?: string } };
    };
    message?: string;
  };
  const meta = e?.response?.data?.meta;
  if (meta?.messageKey === "BACKUP_NO_DEVICES") return tRespaldos("pick.errNoDevices");
  const detail = meta?.message || e?.message || "";
  const base = tRespaldos("pick.errFailed");
  return detail ? `${base} ${detail}` : base;
}

const safeMetaOf = (
  text: string
): { message?: string; messageKey?: string } | null => {
  try {
    return JSON.parse(text)?.meta ?? null;
  } catch {
    return null;
  }
};

export async function archiveDownloadErrorText(err: unknown): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return archiveErrorText(err);
  const meta = safeMetaOf(await data.text().catch(() => ""));
  if (meta?.messageKey === "BACKUP_EXPORT_EMPTY")
    return tRespaldos("detail.export.errEmpty");
  if (meta?.messageKey === "BACKUP_EXPORT_TOO_LARGE")
    return tRespaldos("detail.export.errTooLarge");
  if (meta?.messageKey === "BACKUP_EXPORT_TOO_LARGE_PDF")
    return tRespaldos("detail.export.errTooLargePdf");
  return meta?.message || tRespaldos("detail.export.errFailed");
}

export async function archiveOrAlert(
  companyId: string,
  reason: ArchiveReason,
  sources: ArchiveSource[],
  deviceIds?: string[],
  exportCsv: boolean = true
): Promise<boolean> {
  try {
    await createArchive(companyId, reason, sources, deviceIds, exportCsv);
    return true;
  } catch (err) {
    console.warn("[deviceArchive] createArchive falló:", err);
    await Swal.fire({
      icon: "warning",
      title: tRespaldos("pick.errTitle"),
      text: archiveErrorText(err),
      confirmButtonText: tRespaldos("pick.errOk"),
      confirmButtonColor: "#2563eb",
    });
    return false;
  }
}

export function formatArchiveDate(v: unknown, lang = "es"): string {
  if (v == null) return "—";
  let ms: number | null = null;
  if (typeof v === "number") {
    ms = v < 1e12 ? v * 1000 : v;
  } else if (typeof v === "string") {
    const n = Date.parse(v);
    ms = Number.isNaN(n) ? null : n;
  } else if (typeof v === "object") {
    const o = v as { _seconds?: number; seconds?: number; toDate?: () => Date };
    if (typeof o.toDate === "function") return o.toDate().toLocaleString(lang);
    const s = o._seconds ?? o.seconds;
    if (typeof s === "number") ms = s * 1000;
  }
  if (ms == null || Number.isNaN(ms)) return "—";
  return new Date(ms).toLocaleString(lang);
}

export async function retryArchiveJob(
  jobId: string
): Promise<{ ok: boolean; retried?: number }> {
  const res = await api.post("/device-archive/retry", null, {
    params: { jobId },
  });
  return res.data?.data?.data;
}

export async function fetchArchiveJobs(
  companyId?: string
): Promise<ArchiveJob[]> {
  const res = await api.get("/device-archive/jobs", {
    params: companyId ? { companyId } : undefined,
  });
  return res.data?.data?.data ?? [];
}

export async function fetchArchiveJob(id: string): Promise<ArchiveJobDetail> {
  const res = await api.get("/device-archive/job", { params: { id } });
  return res.data?.data?.data;
}

export async function fetchArchiveFiles(
  query: ArchiveFilesQuery
): Promise<ArchiveFileEntry[]> {
  const params: Record<string, string> = { jobId: query.jobId };
  if (query.deviceId) params.deviceId = query.deviceId;
  if (query.source) params.source = query.source;
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;
  const res = await api.get("/device-archive/files", { params });
  return res.data?.data?.data ?? [];
}

export type ArchiveExportFormat = "excel" | "pdf";

export async function downloadArchiveRange(
  query: ArchiveFilesQuery,
  fileName: string,
  format: ArchiveExportFormat = "excel"
): Promise<void> {
  const params: Record<string, string> = { jobId: query.jobId, format };
  if (query.deviceId) params.deviceId = query.deviceId;
  if (query.source) params.source = query.source;
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;

  const res = await api.get("/device-archive/export", {
    params,
    responseType: "blob",
  });

  const url = URL.createObjectURL(res.data as Blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function deleteArchiveBackup(
  jobId: string,
  deviceId?: string
): Promise<void> {
  const params: Record<string, string> = { jobId };
  if (deviceId) params.deviceId = deviceId;
  await api.delete("/device-archive/backup", { params });
}

// ---------- Cobertura de respaldos en informes ----------
// Días de estos equipos, en el rango, que ya no están en vivo sino en un
// respaldo (suspensión o baja con purga). GET /device-archive/coverage.
export interface ArchiveCoverage {
  deviceId: string;
  jobId: string;
  reason: ArchiveJobReason | null;
  createdAt: number | null;
  days: number;
  firstDay: string; // YYYY-MM-DD (hora de Ciudad de México)
  lastDay: string;
}

export async function fetchArchiveCoverage(deviceIds: string[], from: string, to: string): Promise<ArchiveCoverage[]> {
  const res = await api.get("/device-archive/coverage", { params: { deviceIds: deviceIds.join(","), from, to } });
  return res.data?.data?.data ?? [];
}

/** Resumen para el aviso: cuántos equipos, del primer al último día, y los respaldos involucrados. */
export function summarizeCoverage(list: ArchiveCoverage[]) {
  if (!list.length) return null;
  const days = list.flatMap((c) => [c.firstDay, c.lastDay]).sort();
  return {
    devices: new Set(list.map((c) => c.deviceId)).size,
    firstDay: days[0],
    lastDay: days[days.length - 1],
    jobIds: [...new Set(list.map((c) => c.jobId))],
  };
}
